import { RefObject, useEffect, useState } from 'react'

/**
 * The pixel height that makes `ref` reach the bottom of the viewport.
 *
 * Measured from the element's own top on every layout, NOT a
 * `calc(100dvh - <constant>)`. The constant version needs someone to keep a
 * number in sync with a header, a search bar and a card that all change height
 * independently — which is how `apps/platform` ended up with per-page
 * `topOffset` values that were each wrong by a different amount, and why its
 * shared Table replaced them with exactly this measurement (§22 `fillBottom`).
 *
 * Returns `undefined` until the first measurement, so a caller can fall back to
 * its natural height for one frame rather than flashing at zero.
 *
 * @param reserve px to leave BELOW the element — a docked card, a footer.
 */
export const useFillHeight = (
  ref: RefObject<HTMLElement | null>,
  reserve = 0,
): number | undefined => {
  const [height, setHeight] = useState<number | undefined>(undefined)

  useEffect(() => {
    const el = ref.current
    if (!el) return

    const measure = () => {
      const top = el.getBoundingClientRect().top
      // `visualViewport` where available: on iOS Safari the URL bar collapses on
      // scroll and `innerHeight` does not follow it, so a map sized from
      // innerHeight is short by the bar's height for the rest of the session.
      const viewport = window.visualViewport?.height ?? window.innerHeight
      setHeight(Math.max(240, Math.round(viewport - top - reserve)))
    }

    measure()

    // ResizeObserver rather than a resize listener alone: the element's top
    // moves when anything ABOVE it changes height — the filter bar wrapping to
    // two lines, a chip row appearing — and that fires no window event.
    const ro = new ResizeObserver(measure)
    ro.observe(document.body)
    window.addEventListener('resize', measure)
    window.visualViewport?.addEventListener('resize', measure)

    return () => {
      ro.disconnect()
      window.removeEventListener('resize', measure)
      window.visualViewport?.removeEventListener('resize', measure)
    }
  }, [ref, reserve])

  return height
}
